<!--
	Ported from analytics-design/src/components/Sidebar.tsx, AFTER the SETTE redesign pass
	(light bg-surface sidebar, fixed 248px, no collapse). Two deliberate adaptations from the
	React prototype, both structural (not visual):
	- The sidebar is a normal flex child here instead of `fixed` - the design assumed it was the
	  only sidebar in a full-page app; here it sits inside the existing SETTE layout.
	- The design's own top Header (search/bell/profile) is not ported - the existing SETTE top
	  navigation stays; this panel starts directly at the sidebar+content row.

	The old dark, collapsible (isCollapsed/toggleCollapsed) sidebar - the pre-redesign original -
	is gone, matching the design's own decision log ("Sidebar/Header - отстранета collapse-
	функција"): fixed 248px width + responsive wrap under 900px via plain CSS, no JS toggle.

	The outer div carries ONLY the sette-analytics scope marker - no Tailwind utility classes on
	it. With `important: '.sette-analytics'`, every generated utility selector is
	".sette-analytics .the-class" (a descendant combinator), so a utility class placed on the
	same element as "sette-analytics" never matches anything (Tailwind's own documented caveat
	for this option). The actual flex/background/font utilities live one level down, on a real
	descendant, where the generated selector can match. Height here comes from plain (non-Tailwind)
	CSS in the .scss file instead, for the same reason.
-->
<div class="sette-analytics">
	<div class="flex max-[899px]:flex-wrap h-full bg-page text-ink">
		<!-- Left Analytics Navigation -->
		<aside class="w-[248px] max-[899px]:w-full min-w-0 shrink-0 bg-surface border-r border-line max-[899px]:border-r-0 max-[899px]:border-b p-[24px_16px] flex flex-col">
			<!-- Logo -->
			<div class="p-[4px_12px_24px_12px]">
				@if (logoUrl) {
				<img [src]="logoUrl" alt="SETTE" height="26" class="h-[26px] w-auto" />
				}
			</div>

			<nav class="flex-1 space-y-0.5">
				<!-- Core Analytics Items -->
				@for (item of coreItems; track item.id) {
				<div>
					<button
						type="button"
						(click)="setActiveTab(item.hasSubmenu ? 'coverage_dashboard' : item.id)"
						class="w-full flex items-center h-11 rounded-xl px-3 gap-3 text-[15px] font-medium transition-colors cursor-pointer"
						[class]="(item.hasSubmenu ? isCoverageGroupActive : activeTab === item.id)
							? 'bg-blue-tint text-blue'
							: 'text-ink-muted hover:bg-surface-gray'">
						<mat-icon class="!w-5 !h-5 !text-xl shrink-0">{{ item.icon }}</mat-icon>
						<span class="flex-1 text-left truncate">{{ item.label }}</span>
						@if (item.hasSubmenu) {
						<mat-icon
							class="!w-4 !h-4 !text-base text-ink-subtle shrink-0 transition-transform"
							[class.rotate-180]="isCoverageGroupActive">expand_more</mat-icon>
						}
					</button>

					<!-- Coverage sub-items -->
					@if (item.hasSubmenu && isCoverageGroupActive) {
					<div class="pl-11 pr-2 py-1 space-y-0.5">
						<button
							type="button"
							(click)="setActiveTab('coverage_dashboard')"
							class="w-full text-left h-9 px-3 rounded-xl text-[13px] font-medium transition-colors cursor-pointer"
							[class]="activeTab === 'coverage_dashboard' ? 'bg-blue-tint text-blue' : 'text-ink-muted hover:bg-surface-gray'">
							{{ 'ANALYTICS_NAV.COVERAGE_DASHBOARD' | translate }}
						</button>
						<button
							type="button"
							(click)="setActiveTab('coverage_analysis')"
							class="w-full text-left h-9 px-3 rounded-xl text-[13px] font-medium transition-colors cursor-pointer"
							[class]="activeTab === 'coverage_analysis' ? 'bg-blue-tint text-blue' : 'text-ink-muted hover:bg-surface-gray'">
							{{ 'ANALYTICS_NAV.COVERAGE_ANALYSIS' | translate }}
						</button>
					</div>
					}
				</div>
				}

				<!-- Utilities Section -->
				<div class="pt-4 mt-3 border-t border-line">
					<div class="px-3 mb-1 overline-label text-ink-subtle">{{ 'ANALYTICS_PANEL.UTILITIES' | translate }}</div>
					@for (item of utilityItems; track item.id) {
					<button
						type="button"
						(click)="setActiveTab(item.id)"
						class="w-full flex items-center h-11 rounded-xl px-3 gap-3 text-[15px] font-medium transition-colors cursor-pointer"
						[class]="activeTab === item.id ? 'bg-blue-tint text-blue' : 'text-ink-muted hover:bg-surface-gray'">
						<mat-icon class="!w-5 !h-5 !text-xl shrink-0">{{ item.icon }}</mat-icon>
						<span class="flex-1 text-left truncate">{{ item.label }}</span>
					</button>
					}
				</div>
			</nav>

			<!-- Client block -->
			<div class="mt-3 bg-surface-gray rounded-2xl p-3 flex items-center gap-3">
				<div class="w-9 h-9 rounded-[10px] bg-blue flex items-center justify-center shrink-0">
					<mat-icon class="!w-5 !h-5 !text-xl text-on-blue">insights</mat-icon>
				</div>
				<div class="min-w-0">
					<div class="caption text-ink-subtle">{{ 'ANALYTICS_GLOSSARY.CLIENT' | translate }}</div>
					<div class="text-[14px] font-medium text-ink truncate">{{ clientName || '—' }}</div>
				</div>
			</div>
		</aside>

		<!-- Main Content. The p-4/max-w wrapper matches the design's own DashboardContent root
		     (which wrapped its FilterBar + content together) - individual screens below (e.g.
		     OverviewComponent) own only their internal spacing, not this outer padding, so it's
		     never doubled. -->
		<main class="flex-1 min-w-0 overflow-y-auto max-[899px]:w-full">
			<div class="p-4 space-y-4 max-w-[1600px] mx-auto">
				<app-filter-bar
					[filters]="filters"
					[brandOptions]="brandOptions"
					[mediaOutletOptions]="mediaOutletOptions"
					[authorOptions]="authorOptions"
					[screen]="activeTab"
					[coverageAnalysisTab]="currentAnalysisMetric"
					[pdfExporting]="pdfExporting"
					(filtersChange)="onFiltersChange($event)"
					(exportPdfRequested)="exportScreenAsPdf()"></app-filter-bar>

				<div #screenContent class="space-y-4">
				@if (activeTab === 'overview') {
				<app-overview [filters]="filters" [brandOptions]="rawBrandTags" (navigate)="onPanelNavigate($event)"></app-overview>
				} @else if (activeTab === 'coverage_dashboard') {
				<app-coverage-dashboard [filters]="filters" [brandOptions]="rawBrandTags" (navigate)="onPanelNavigate($event)"></app-coverage-dashboard>
				} @else if (activeTab === 'coverage_analysis') {
				<app-coverage-analysis [filters]="filters" [brandOptions]="rawBrandTags" [initialMetric]="analysisInitialMetric" (metricChange)="onAnalysisMetricChange($event)" (navigate)="onPanelNavigate($event)"></app-coverage-analysis>
				} @else if (activeTab === 'brands') {
				<app-brands [filters]="filters" [brandOptions]="rawBrandTags" (navigate)="onPanelNavigate($event)"></app-brands>
				} @else if (activeTab === 'topics') {
				<app-topics [filters]="filters" [brandOptions]="rawBrandTags" (navigate)="onPanelNavigate($event)"></app-topics>
				} @else if (activeTab === 'sentiment') {
				<app-sentiment [filters]="filters" [brandOptions]="rawBrandTags" (navigate)="onPanelNavigate($event)"></app-sentiment>
				} @else if (activeTab === 'media') {
				<app-media [filters]="filters" [brandOptions]="rawBrandTags" (navigate)="onPanelNavigate($event)"></app-media>
				} @else if (activeTab === 'reports') {
				<app-reports></app-reports>
				} @else if (activeTab === 'saved_views' || activeTab === 'exports') {
				<app-analytics-utilities
					[initialSection]="activeTab === 'saved_views' ? 'saved-views' : 'exports'"
					[brandOptions]="brandOptions"
					(navigate)="onPanelNavigate($event)"
					(openSavedView)="onOpenSavedView($event)"></app-analytics-utilities>
				} @else {
				<h1 class="page-title">{{ activeTitle }}</h1>
				}
				</div>
			</div>
		</main>
	</div>
</div>
